import { EntityEmptyState } from '@pesto/features/src/EntityProfile/components/EntityEmptyState';
import { EntityErrorBanner } from '@pesto/features/src/EntityProfile/components/EntityErrorBanner';
import { StatementDetailView } from '@pesto/features/src/Statements/components/StatementDetailView';
import { sortByBillingPeriod } from '@pesto/features/src/Statements/components/StatementPeriodNav';
import { StatementsList } from '@pesto/features/src/Statements/StatementsList';
import { toStatementListItem } from '@pesto/features/src/Statements/utils/toStatementListItem';
import { LoadingCard } from '@pesto/ui';
import { FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useGetLoanStatementsQuery } from '../../../../__generated__/graphql/api';

interface LoanStatementsTabProps {
  loanId: string;
  statementId?: string;
  statementsBasePath: string;
}

export const LoanStatementsTab = ({ loanId, statementId, statementsBasePath }: LoanStatementsTabProps) => {
  const navigate = useNavigate();

  const { data, loading, error } = useGetLoanStatementsQuery({
    variables: { loanId },
    notifyOnNetworkStatusChange: true,
  });

  const rows = (data?.loanStatements ?? [])
    .filter((s): s is NonNullable<typeof s> => Boolean(s))
    .map(toStatementListItem);

  const statements = sortByBillingPeriod(rows);
  const statement = statements.find((item) => item.id === statementId) ?? null;
  const statementsPath = statementsBasePath;

  if (loading && rows.length === 0) {
    return <LoadingCard />;
  }

  if (error) {
    return <EntityErrorBanner message="We couldn't load statements." detail={error.message} />;
  }

  if (rows.length === 0) {
    return <EntityEmptyState icon={FileText} title="This customer has no statements on file." />;
  }

  if (statementId) {
    return (
      <StatementDetailView
        statementId={statementId}
        statement={statement}
        statements={statements}
        loading={loading}
        onBack={() => navigate(statementsPath)}
        onSelectStatement={(id) => navigate(`${statementsPath}/${id}`)}
        getLoanHref={(id) => `/loans/${id}`}
      />
    );
  }

  return (
    <StatementsList
      rows={rows}
      loading={loading}
      enableDateRangeFilter
      onOpenStatement={(id) => navigate(`${statementsPath}/${id}`)}
      getLoanHref={(id) => `/loans/${id}`}
    />
  );
};
